import React from 'react';
import { useTranslation } from 'react-i18next';
import Portfolio from '../../components/Portfolio';
import styled from 'styled-components';
import { theme } from '../../theme';

const PageHero = styled.section`
  padding: 140px 2rem 80px;
  background: linear-gradient(180deg, ${theme.colors.bg} 0%, #101722 100%);
  position: relative;
  overflow: hidden;
  border-bottom: 1px solid rgba(148, 163, 184, 0.12);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(rgba(148, 163, 184, 0.08) 1px, transparent 1px),
      linear-gradient(90deg, rgba(148, 163, 184, 0.08) 1px, transparent 1px);
    background-size: 56px 56px;
    mask-image: linear-gradient(to bottom, rgba(0, 0, 0, 0.45), transparent 78%);
    opacity: 0.22;
  }
`;

const Inner = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(280px, 360px);
  gap: 2rem;
  align-items: end;

  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const Copy = styled.div``;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.45rem 0.8rem;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${theme.colors.textSecondary};
  background: rgba(15, 23, 42, 0.7);
  border: 1px solid rgba(148, 163, 184, 0.18);
  margin-bottom: 1.25rem;
`;

const Title = styled.h1`
  font-size: clamp(2rem, 5vw, 3.5rem);
  font-weight: 700;
  letter-spacing: -0.06em;
  line-height: 1.05;
  color: ${theme.colors.text};
  margin-bottom: 1rem;
  max-width: 760px;

  span {
    background: ${theme.colors.gradient};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const Sub = styled.p`
  font-size: 1.05rem;
  color: ${theme.colors.textMuted};
  max-width: 620px;
  margin: 0;
  line-height: 1.75;
`;

const LeadCard = styled.aside`
  padding: 1.2rem;
  border-radius: 24px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  background: linear-gradient(180deg, rgba(17, 25, 34, 0.96), rgba(10, 15, 22, 0.9));

  span {
    display: inline-flex;
    margin-bottom: 0.45rem;
    color: ${theme.colors.primaryLight};
    font-family: 'Space Grotesk', 'Manrope', sans-serif;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
  }

  h3 {
    margin-bottom: 0.5rem;
    font-size: 1.3rem;
    font-weight: 700;
    letter-spacing: -0.04em;
    color: ${theme.colors.text};
  }

  p {
    color: ${theme.colors.textMuted};
    font-size: 0.92rem;
    line-height: 1.7;
    margin-bottom: 0.85rem;
  }
`;

const Metrics = styled.div`
  display: grid;
  gap: 0.65rem;
`;

const Metric = styled.div`
  padding: 0.85rem 0.95rem;
  border-radius: 18px;
  border: 1px solid rgba(148, 163, 184, 0.1);
  background: rgba(8, 12, 18, 0.62);

  span {
    display: block;
    margin-bottom: 0.3rem;
    color: ${theme.colors.textMuted};
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  strong {
    color: ${theme.colors.textSecondary};
    font-size: 0.88rem;
    line-height: 1.45;
  }
`;

const PortfolioPage: React.FC = () => {
  const { t } = useTranslation();

  const metrics = [
    { label: t('portfolioPage.metric0Label'), value: t('portfolioPage.metric0Value') },
    { label: t('portfolioPage.metric1Label'), value: t('portfolioPage.metric1Value') },
    { label: t('portfolioPage.metric2Label'), value: t('portfolioPage.metric2Value') },
  ];

  return (
    <>
      <PageHero>
        <Inner>
          <Copy>
            <Tag>{t('portfolioPage.tag')}</Tag>
            <Title>
              {t('portfolioPage.title')} <span>{t('portfolioPage.titleGradient')}</span>
            </Title>
            <Sub>{t('portfolioPage.subtitle')}</Sub>
          </Copy>

          <LeadCard>
            <span>{t('portfolioPage.leadLabel')}</span>
            <h3>{t('portfolioPage.leadTitle')}</h3>
            <p>{t('portfolioPage.leadText')}</p>
            <Metrics>
              {metrics.map((metric) => (
                <Metric key={metric.label}>
                  <span>{metric.label}</span>
                  <strong>{metric.value}</strong>
                </Metric>
              ))}
            </Metrics>
          </LeadCard>
        </Inner>
      </PageHero>
      <Portfolio />
    </>
  );
};

export default PortfolioPage;
